import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
export function ResetPasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const {
    updatePassword
  } = useAuth();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    if (!newPassword || !confirmPassword) {
      setError('Por favor completa ambos campos.');
      return;
    }
    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await updatePassword(newPassword);
      if (res.success) {
        setSuccessMsg(res.message);
        setTimeout(() => {
          navigate('/area-usuario/login', {
            replace: true
          });
        }, 2500);
      } else {
        setError(res.error || 'Error al actualizar la contraseña.');
      }
    } catch (err) {
      setError('Ocurrió un error inesperado.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-[#0A0A0B] font-sans text-white selection:bg-gold/30 selection:text-gold flex flex-col relative overflow-hidden" data-id="element-868">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-869">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-gold/5 rounded-full blur-[120px]" data-id="element-870" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-gold/5 rounded-full blur-[120px]" data-id="element-871" />
      </div>

      {/* Header */}
      <header className="relative z-10 p-6 sm:p-8 flex items-center justify-center" data-id="element-872">
        <div className="font-serif italic text-gold text-lg sm:text-xl font-bold tracking-[2px]" data-id="element-873">
          EL CÓDIGO 888
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 relative z-10" data-id="element-874">
        <div className="w-full max-w-[420px]" data-id="element-875">
          <div className="text-center mb-8" data-id="element-876">
            <h1 className="font-serif text-3xl sm:text-4xl font-black text-white mb-3" data-id="element-877">
              Nueva contraseña
            </h1>
            <p className="text-white/60 text-sm" data-id="element-878">
              Ingresa tu nueva contraseña para restablecer el acceso a tu
              cuenta.
            </p>
          </div>

          <div className="bg-[#0c0c0e]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-6 sm:p-8 shadow-[0_0_40px_rgba(0,0,0,0.5)] relative overflow-hidden" data-id="element-879">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gold/50 to-transparent opacity-50" data-id="element-880" />

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-881">
              <div data-id="element-882">
                <label className="block text-white/60 text-xs font-bold uppercase tracking-wider mb-2 ml-1" data-id="element-883">
                  Nueva contraseña
                </label>
                <div className="relative" data-id="element-884">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-885">
                    <Lock className="h-4 w-4 text-white/40" data-id="element-886" />
                  </div>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl py-3.5 pl-11 pr-4 text-white placeholder:text-white/20 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-all" placeholder="Mínimo 6 caracteres" disabled={isSubmitting || !!successMsg} data-id="element-887" />
                </div>
              </div>

              <div data-id="element-888">
                <label className="block text-white/60 text-xs font-bold uppercase tracking-wider mb-2 ml-1" data-id="element-889">
                  Confirmar contraseña
                </label>
                <div className="relative" data-id="element-890">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-891">
                    <Lock className="h-4 w-4 text-white/40" data-id="element-892" />
                  </div>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl py-3.5 pl-11 pr-4 text-white placeholder:text-white/20 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/50 transition-all" placeholder="Repite tu contraseña" disabled={isSubmitting || !!successMsg} data-id="element-893" />
                </div>
              </div>

              {error && <motion.p initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-red-400 text-xs font-medium text-center bg-red-400/10 py-2 rounded-lg border border-red-400/20" data-id="element-894">
                  {error}
                </motion.p>}

              {successMsg && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex items-center justify-center gap-2 text-green-400 text-xs font-medium text-center bg-green-400/10 py-3 px-3 rounded-lg border border-green-400/20" data-id="element-895">
                  <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-896" />
                  <span data-id="element-897">{successMsg} Redirigiendo al login...</span>
                </motion.div>}

              <button type="submit" disabled={isSubmitting || !!successMsg} className="w-full bg-gold text-dark font-black text-sm py-4 rounded-xl shadow-[0_5px_20px_rgba(212,175,55,0.25)] hover:bg-yellow-400 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-70 disabled:pointer-events-none flex items-center justify-center gap-2 mt-2 uppercase tracking-wider" data-id="element-898">
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-899" /> : 'Actualizar Contraseña'}
              </button>
            </form>

            <p className="text-center text-white/20 text-[10px] mt-6" data-id="element-900">
              🔒 Conexión segura con Supabase Auth
            </p>
          </div>
        </div>
      </main>
    </div>;
}